import type { MinCopy } from '@/i18n/types'

export const minPl: MinCopy = {
  brand: {
    name: 'Lexio',
    tagline: 'Rozumiej angielski, gdy czytasz',
    beta: 'Beta',
  },
  nav: {
    today: 'Dziś',
    catalog: 'Katalog',
    words: 'Słownik',
    reading: 'Czytanie',
    refresh: 'Powtórki',
    challenge: 'Sprawdzenie',
    progress: 'Postęp',
    practice: 'Ćwiczenia',
    settings: 'Ustawienia',
    feedback: 'Feedback',
    faq: 'FAQ',
    mobileWords: 'Słowa',
    mobileReview: 'Powtórki',
    more: 'Więcej',
    logout: 'Wyloguj',
    mobileNavAria: 'Nawigacja mobilna',
    closeMenuAria: 'Zamknij menu',
    close: 'Zamknij',
  },
  auth: {
    localeSwitcherLabel: 'Język interfejsu',
    loginTitle: 'Lexio',
    loginDescription: 'Zaloguj się, aby kontynuować naukę języka.',
    email: 'Email',
    password: 'Hasło',
    forgotPassword: 'Nie pamiętasz hasła?',
    signIn: 'Zaloguj się',
    or: 'lub',
    continueWithGoogle: 'Kontynuuj z Google',
    noAccount: 'Nie masz konta?',
    registerLink: 'Rejestracja',
    invalidEmail: 'Nieprawidłowy adres email',
    passwordRequired: 'Hasło jest wymagane',
    googleAuthFailed: 'Nie udało się zalogować przez Google. Spróbuj email i hasło albo wróć później.',
    unableSignIn: 'Nie można się teraz zalogować. Spróbuj ponownie.',
    invalidCredentials: 'Nieprawidłowy email lub hasło',
    tooManyAttempts: 'Zbyt wiele prób. Spróbuj później.',
    registerTitle: 'Utwórz konto Lexio',
    registerDescription: 'Słownik, czytanie i powtórki — w jednym miejscu.',
    displayName: 'Nazwa wyświetlana',
    confirmPassword: 'Potwierdź hasło',
    createAccount: 'Utwórz konto',
    agreePrefix: 'Tworząc konto, akceptujesz',
    termsOfService: 'Regulamin',
    agreeAnd: 'oraz',
    privacyPolicy: 'Politykę prywatności',
    agreeSuffix: '.',
    haveAccount: 'Masz już konto?',
    signInLink: 'Zaloguj się',
    passwordMin: 'Hasło musi mieć co najmniej 8 znaków',
    passwordUpper: 'Wymagana co najmniej 1 wielka litera',
    passwordDigit: 'Wymagana co najmniej 1 cyfra',
    passwordsMismatch: 'Hasła się nie zgadzają',
    unableRegister: 'Nie można teraz utworzyć konta. Spróbuj ponownie.',
    emailAlreadyRegistered: 'Ten email jest już zarejestrowany',
    resetTitle: 'Zresetuj hasło',
    resetDescription: 'Podaj adres email — wyślemy link do resetu.',
    sendResetLink: 'Wyślij link',
    backTo: 'Wróć do',
    resetSent:
      'Jeśli konto z tym emailem istnieje, wkrótce dostaniesz link. Sprawdź też folder Spam.',
    resetTooMany: 'Zbyt wiele żądań z tego urządzenia. Poczekaj kilka minut.',
    resetUnable: 'Nie można teraz wysłać linku. Spróbuj później.',
    chooseNewPasswordTitle: 'Wybierz nowe hasło',
    chooseNewPasswordDescription: 'Ustaw silne hasło, aby odzyskać dostęp do konta.',
    newPassword: 'Nowe hasło',
    confirmNewPassword: 'Potwierdź nowe hasło',
    saveNewPassword: 'Zapisz hasło',
    rememberedPassword: 'Przypomniałeś sobie hasło?',
    resetLinkInvalid: 'Link do resetu jest nieprawidłowy lub wygasł.',
    resetUnableConfirm: 'Nie można teraz zresetować hasła. Spróbuj później.',
    passwordResetSuccess: 'Hasło zostało zmienione. Zaloguj się.',
    checkEmailTitle: 'Sprawdź skrzynkę',
    verifyingTitle: 'Potwierdzamy email',
    verifyingMessage: 'Potwierdzamy email…',
    verificationSent:
      'Wysłaliśmy link weryfikacyjny na Twój email. Sprawdź też folder Spam.',
    emailPlaceholder: 'you@example.com',
    sendVerificationLink: 'Wyślij nowy link weryfikacyjny',
    alreadyVerified: 'Już potwierdzone?',
    verifyingHint: 'Nie zamykaj tej strony, póki aktywujemy konto.',
    verificationLinkInvalid: 'Link weryfikacyjny jest nieprawidłowy lub wygasł. Poproś o nowy poniżej.',
    enterRegisteredEmail: 'Podaj email użyty przy rejestracji.',
    verificationResent:
      'Jeśli konto nadal wymaga weryfikacji, wysłaliśmy nowy link. Sprawdź też Spam.',
    verificationTooMany: 'Zbyt wiele żądań. Spróbuj później.',
    verificationUnable: 'Nie można teraz wysłać linku. Spróbuj ponownie.',
    signingInWithGoogle: 'Logowanie przez Google…',
  },
  onboarding: {
    wizardTitle: 'Witaj w Lexio',
    wizardSubtitle: 'Powiedz nam trochę o sobie, żebyśmy dopasowali naukę.',
    stepUiTitle: 'W jakim języku ma być aplikacja?',
    stepUiDescription: 'Menu, przyciski i intro będą w tym języku. Później zmienisz to w Ustawieniach.',
    uiLanguageLabel: 'Język interfejsu',
    stepNativeTitle: 'Jaki jest Twój język nauki?',
    stepNativeDescription: 'Używamy go do tłumaczeń, podpowiedzi w tekście i wyjaśnień AI.',
    stepThemeTitle: 'Jak ma wyglądać Lexio?',
    stepThemeDescription: 'Wybierz jasny lub ciemny motyw — później zmienisz to w Ustawieniach.',
    stepLevelTitle: 'Jaki jest Twój poziom angielskiego?',
    stepLevelDescription: 'Artykuły, powtórki i ćwiczenia dopasujemy do tego poziomu.',
    stepDurationTitle: 'Ile czasu chcesz uczyć się dziennie?',
    stepDurationDescription: 'Codzienna sesja dopasuje się do tego czasu.',
    stepDurationLabel: '{minutes} min',
    stepTopicsTitle: 'O czym lubisz czytać?',
    stepTopicsDescription: 'Wybierz kilka tematów — na ich podstawie polecimy artykuły. Możesz pominąć.',
    stepTopicsSkip: 'Pomiń na razie',
    stepCompleteTitle: 'Gotowe!',
    stepCompleteDescription: 'Lexio dopasuje czytanie, słownik i codzienne sesje do Twoich odpowiedzi.',
    stepCompleteHint: 'Wszystko możesz zmienić w Ustawieniach w dowolnym momencie.',
    next: 'Dalej',
    back: 'Wstecz',
    finish: 'Zacznij naukę',
    languageLabel: 'Język nauki',
    levelLabel: 'Poziom',
    saveFailed: 'Nie udało się zapisać ustawień. Spróbuj ponownie.',
  },
  learningDemo: {
    skip: 'Pomiń intro',
    introTitle: 'Jeden dzień nauki',
    introPhilosophy:
      'Nie musisz zakuwać tysięcy słów. Czytaj, zapisuj to, co ważne — a Lexio pomoże przypomnieć to we właściwym momencie.',
    introCta: 'Pokaż jak',
    questStep: 'Krok {current} z {total}',
    readTitle: 'Czytaj',
    readBody: 'Przeczytaj krótki tekst. Gdy napotkasz nieznane słowo — kliknij je.',
    understandTitle: 'Rozumiej',
    understandBody: 'Wyjaśniamy słowo w kontekście. Jeśli pomaga — zapisz je do słownika.',
    vocabularyTitle: 'Twój słownik',
    vocabularyBody: 'Zapisane słowa są tutaj — to Twoja osobista lista do nauki.',
    refreshTitle: 'Powtarzaj',
    refreshBody: 'Później dziś lub jutro wróć do powtórek. Powtórki rozłożone w czasie pomagają słowom zostać w pamięci.',
    todayTitle: 'Dziś',
    todayBody: 'Twoja baza na dzień — przygotujemy, co czytać i co powtórzyć według postępów.',
    tapWordHint: 'Kliknij podświetlone słowo',
    saveWord: 'Zapisz słowo',
    saving: 'Zapisuję…',
    saved: 'Zapisano!',
    savedWordLabel: 'Twoje pierwsze słowo',
    vocabularyStatusNew: 'Nowe',
    continue: 'Dalej',
    completeTitle: 'Jesteś gotowy',
    completeBody: 'Słownik już się zaczął. Otwórz pierwszy artykuł i kliknij nieznane słowo.',
    completeCta: 'Zacznij czytać',
    replay: 'Powtórz intro',
    replayDescription: 'Przejdź krótkie wprowadzenie jeszcze raz.',
    readLabel: 'Czytaj',
    understandLabel: 'Rozumiej',
    means: 'Znaczenie',
    inContext: 'W tym zdaniu',
    definitionLabel: 'Definicja',
    exampleLabel: 'Przykład',
    vocabularyMoreHint:
      'Słownik śledzi też postęp, historię powtórek i osobiste notatki — to więcej niż zwykła lista słów.',
    refreshFlipHint: 'Kliknij kartę, aby odwrócić',
    refreshCardLabel: 'Karta powtórki',
    refreshMoreHint:
      'Powtórki mają też kilka trybów, statystyki sesji i harmonogram SM-2 — to tylko szybki podgląd.',
    todayPreviewHint: 'Typowy dzień wygląda tak:',
    todayCardLabel: 'Twój dzień',
    todayStepReading: 'Kontynuuj czytanie',
    todayStepReadingHint: 'Weź artykuł na swoim poziomie',
    todayStepNewWords: 'Zapisuj nowe słowa',
    todayStepNewWordsHint: 'Klikaj słowa podczas czytania, by rozbudować listę',
    todayStepRefresh: 'Odśwież pamięć',
    todayStepRefreshHint: 'Powtarzaj zaległe słowa, aby zostały',
    todayMoreHint: '„Dziś” łączy czytanie, słownik i powtórki w jednym miejscu.',
    completeHelpHint:
      'Pytania? Otwórz FAQ w menu bocznym (na telefonie — w „Więcej”). Pomysł lub błąd — stuknij Feedback tuż nad FAQ.',
    translation: 'Tłumaczenie',
    playPronunciation: 'Wymowa',
    demoArticleTitle: 'Przed szkołą',
    saveWordFailed: 'Nie udało się zapisać słowa. Spróbuj ponownie.',
    loadExplanationFailed: 'Nie udało się wczytać wyjaśnienia. Spróbuj ponownie.',
    loadingExplanation: 'Ładowanie wyjaśnienia…',
  },
  faq: {
    title: 'Częste pytania',
    intro: 'Krótko o tym, jak Lexio działa dziś — czytanie, zapisywanie słów, powtórki i konto.',
    feedbackTitle: 'Napisz, co myślisz',
    feedbackHint: 'Plusy, tarcia, błędy, pomysły — czytamy każdą wiadomość.',
    breadcrumb: 'FAQ',
    items: [
      {
        id: 'what-is-lexio',
        question: 'Czym jest Lexio?',
        answer:
          'Lexio pomaga rozumieć angielski podczas czytania. Kliknij słowo lub frazę — dostaniesz jasne wyjaśnienie w kontekście, zapisz to, co ważne, i spotkaj to ponownie w powtórkach rozłożonych w czasie. Dzięki temu czytasz dalej zamiast otwierać Google.',
      },
      {
        id: 'how-to-start',
        question: 'Jak zacząć?',
        answer:
          'Utwórz darmowe konto, potwierdź email, wybierz język ojczysty i poziom, przejdź krótkie demo. Potem otwórz Czytanie, zapisuj nieznane słowa i rób Powtórki, gdy karty są gotowe.',
      },
      {
        id: 'tap-understand',
        question: 'Jak działa „kliknij — zrozum”?',
        answer:
          'W artykule kliknij słowo (lub zaznacz krótką frazę). Lexio pokaże znaczenie pasujące do tego zdania, powiązane formy, gdy to pomaga, oraz inne znaczenia przy wieloznaczności. Przed zapisaniem wybierz znaczenie, którego chcesz się uczyć.',
      },
      {
        id: 'save-meaning',
        question: 'Co się dzieje, gdy zapisuję słowo?',
        answer:
          'Słowo trafia do słownika jako jedna karta. Jeśli później dodasz inne znaczenie — rozszerzy tę samą kartę, bez duplikatów. Powtórki używają tej karty — ćwiczysz to, co naprawdę zapisałeś.',
      },
      {
        id: 'reviews',
        question: 'Jak działają powtórki?',
        answer:
          'Lexio używa powtórek rozłożonych w czasie (SM-2). Trudniejsze słowa wracają wcześniej, łatwiejsze — później. Otwórz Powtórki z ekranu „Dziś” lub z menu, gdy masz karty do terminu — to nawyk, który utrwala czytanie.',
      },
      {
        id: 'email-verify',
        question: 'Po co weryfikować email?',
        answer:
          'Weryfikacja chroni konta przed fałszywymi rejestracjami. Po rejestracji emailem otwórz link z wiadomości (sprawdź Spam). Logowanie Google jest już zweryfikowane i pomija ten krok.',
      },
      {
        id: 'forgot-password',
        question: 'Zapomniałem hasła. Co robić?',
        answer:
          'Na stronie logowania wybierz „Nie pamiętasz hasła?”, podaj email i otwórz link z wiadomości. Link ważny godzinę. Jeśli nic nie przyszło — sprawdź Spam i ten sam email co przy koncie.',
      },
      {
        id: 'wrong-translation',
        question: 'Co jeśli tłumaczenie wygląda źle?',
        answer:
          'Najpierw sprawdź „Inne znaczenia” w podpowiedzi — sens z kontekstu jest oznaczony jako „W tym zdaniu”. Jeśli nadal źle — napisz przez Ustawienia → Feedback lub formularz na tej stronie. Błędne lookupy pomagają ulepszać Trust Ops.',
      },
      {
        id: 'mobile',
        question: 'Czy Lexio działa na telefonie?',
        answer:
          'Tak — to mobilna aplikacja webowa. Możesz dodać ją do ekranu głównego. Dolne menu: Dziś, Czytanie, Słownik i Powtórki. Treść przewija się nad menu, akcje zostają w zasięgu kciuka.',
      },
      {
        id: 'free',
        question: 'Czy Lexio jest darmowe?',
        answer:
          'Tak dla głównej pętli: czytanie, zrozumienie po kliknięciu, słownik i powtórki. Część funkcji AI może mieć dzienne limity. Plany płatne mogą pojawić się później; darmowa ścieżka pozostanie podstawą codziennego rozumienia angielskiego.',
      },
      {
        id: 'languages',
        question: 'Jakie języki są obsługiwane?',
        answer:
          'Czytasz angielski. Tłumaczenia dydaktyczne są w języku nauki (rosyjski, ukraiński lub polski). Interfejs też może być angielski, rosyjski, ukraiński lub polski — wybierz na stronie startowej albo zachowaj język z rejestracji.',
      },
    ],
  },
  feedback: {
    like: 'Podoba mi się',
    dislike: 'To jest mylące',
    bug: 'Coś się zepsuło',
    idea: 'Pomysł',
    placeholder: 'Co powinniśmy wiedzieć?',
    emailPlaceholder: 'Twój email',
    submit: 'Wyślij opinię',
    done: 'Dzięki — otrzymaliśmy Twoją opinię.',
    sendAnother: 'Kolejna wiadomość',
    error: 'Nie udało się wysłać. Spróbuj za chwilę.',
    settingsTitle: 'Feedback',
    settingsHint: 'Napisz, co pomaga, co myli albo co się zepsuło.',
  },
  settings: {
    appearanceTitle: 'Wygląd',
    appearanceDescription: 'Motyw i język interfejsu.',
    uiLanguageLabel: 'Język interfejsu',
    uiLanguageHint: 'Menu, przyciski i nawigacja. Osobno od języka nauki.',
    themeLabel: 'Motyw',
    themeLight: 'Jasny',
    themeDark: 'Ciemny',
    learningLanguageLabel: 'Język nauki',
    learningLanguageHint: 'Do tłumaczeń i wyjaśnień w Twoim języku.',
  },
}
